<template>
    <div class="debug-container">
        <h1 class="text-h3 text-primary">🎵 Sequence Pitch Matcher Debug</h1>
        <p class="text-body-2">
            Test the SequencePitchMatcher utility in isolation.
            Configure a sequence, start listening, and play the notes to
            see how matching works.
        </p>

        <!-- 3-Column Layout -->
        <div class="layout">
            <!-- Left Panel: Configuration -->
            <div class="left-panel bg-surface">
                <v-card>
                    <v-card-title>Configuration</v-card-title>
                    <v-card-text>
                        <v-text-field
                            v-model="sequenceInput"
                            label="Sequence (comma-separated)"
                            hint="Example: C4, D4, E4, F4, G4"
                            persistent-hint
                            :disabled="isListening"
                        />

                        <v-slider
                            v-model="toleranceCents"
                            label="Tolerance (cents)"
                            min="10"
                            max="45"
                            step="5"
                            thumb-label
                            hint="Max 45 to prevent overlap (creates dead zone between notes)"
                            persistent-hint
                            :disabled="isListening"
                        />

                        <v-checkbox
                            v-model="allowOctaveErrors"
                            label="Allow octave errors"
                            hint="Accept C5 when expecting C4"
                            persistent-hint
                            :disabled="isListening"
                        />

                        <v-slider
                            v-model="dropoutBufferFrames"
                            label="Dropout Buffer (frames)"
                            min="0"
                            max="20"
                            step="1"
                            thumb-label
                            hint="Frames of silence to ignore before ending note (prevents hair-trigger)"
                            persistent-hint
                            :disabled="isListening"
                        />

                        <div class="mt-4">
                            <v-btn
                                v-if="!isListening"
                                color="primary"
                                @click="startListening"
                                block
                            >
                                Start Listening
                            </v-btn>
                            <v-btn
                                v-else
                                color="error"
                                @click="stopListening"
                                block
                            >
                                Stop Listening
                            </v-btn>
                        </div>

                        <!-- Pitch Recorder -->
                        <div class="mt-4 pa-3" style="background: rgba(0,0,0,0.03); border-radius: 8px;">
                            <div class="text-caption text-medium-emphasis mb-2">Recording</div>
                            <PitchRecorderWidget
                                ref="recorderWidget"
                                exercise-type="sequence-matcher"
                                :exercise-config="{
                                    sequence: sequence,
                                    toleranceCents,
                                    allowOctaveErrors,
                                    dropoutBufferFrames
                                }"
                            />
                        </div>
                        <v-btn
                            class="mt-2"
                            color="secondary"
                            @click="resetMatcher"
                            :disabled="!isListening"
                            block
                        >
                            Reset Sequence
                        </v-btn>
                    </v-card-text>
                </v-card>
            </div>

            <!-- Center Panel: Sequence Visualization -->
            <div class="center-panel bg-surface">
                <h2 class="text-h5 text-secondary">Sequence Progress</h2>
                <v-card>
                    <v-card-text>
                        <div class="sequence-display">
                            <div
                                v-for="(note, index) in sequence"
                                :key="index"
                                class="note-box"
                                :class="{
                                    completed: isNoteCompleted(index),
                                    current: isCurrentNote(index),
                                    playing: isNotePlaying(index),
                                    pending:
                                        !isNoteCompleted(index) &&
                                        !isCurrentNote(index) &&
                                        !isNotePlaying(index),
                                }"
                            >
                                <div class="note-label">{{ note }}</div>
                                <div class="note-status">
                                    <v-icon
                                        v-if="isNoteCompleted(index)"
                                        color="success"
                                        >mdi-check-circle</v-icon
                                    >
                                    <v-icon
                                        v-else-if="isCurrentNote(index)"
                                        color="primary"
                                        >mdi-arrow-right-circle</v-icon
                                    >
                                    <v-icon
                                        v-else-if="isNotePlaying(index)"
                                        color="warning"
                                        >mdi-music-note</v-icon
                                    >
                                    <v-icon v-else color="grey"
                                        >mdi-circle-outline</v-icon
                                    >
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <h2 class="text-h5 text-secondary mt-6">Pitch Detection</h2>
                <v-card>
                    <v-card-text>
                        <v-list dense>
                            <v-list-item>
                                <v-list-item-title
                                    >Listening:</v-list-item-title
                                >
                                <v-list-item-subtitle>{{
                                    isListening ? "Yes" : "No"
                                }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title
                                    >Detected Note:</v-list-item-title
                                >
                                <v-list-item-subtitle class="text-h6">
                                    {{ detectedPitch?.note || "—" }}
                                </v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title
                                    >Frequency:</v-list-item-title
                                >
                                <v-list-item-subtitle>
                                    {{
                                        detectedPitch?.frequency?.toFixed(2) ||
                                        "—"
                                    }}
                                    Hz
                                </v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title
                                    >Cents Off:</v-list-item-title
                                >
                                <v-list-item-subtitle>
                                    {{
                                        detectedPitch?.centsOff?.toFixed(1) ||
                                        "—"
                                    }}
                                    cents
                                </v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title
                                    >Confidence:</v-list-item-title
                                >
                                <v-list-item-subtitle>
                                    {{
                                        detectedPitch
                                            ? (
                                                  detectedPitch.confidence * 100
                                              ).toFixed(0) + "%"
                                            : "—"
                                    }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </div>

            <!-- Right Panel: Matcher State & Event Log -->
            <div class="right-panel bg-surface">
                <h2 class="text-h5 text-secondary">Matcher State</h2>
                <v-card>
                    <v-card-text>
                        <v-list dense>
                            <v-list-item>
                                <v-list-item-title
                                    >Current Index:</v-list-item-title
                                >
                                <v-list-item-subtitle>{{
                                    state.currentIndex
                                }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Progress:</v-list-item-title>
                                <v-list-item-subtitle
                                    >{{
                                        (state.progress * 100).toFixed(0)
                                    }}%</v-list-item-subtitle
                                >
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title
                                    >Is Complete:</v-list-item-title
                                >
                                <v-list-item-subtitle>{{
                                    state.isComplete
                                }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title
                                    >Wrong Attempts:</v-list-item-title
                                >
                                <v-list-item-subtitle>{{
                                    state.wrongAttempts
                                }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title
                                    >Current Note:</v-list-item-title
                                >
                                <v-list-item-subtitle
                                    class="text-h6 text-primary"
                                >
                                    {{ currentNote }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>

                        <v-progress-linear
                            :model-value="state.progress * 100"
                            color="primary"
                            height="25"
                            class="mt-4"
                        >
                            {{ (state.progress * 100).toFixed(0) }}%
                        </v-progress-linear>
                    </v-card-text>
                </v-card>

                <h2 class="text-h5 text-secondary mt-6">Event Log</h2>
                <v-card>
                    <v-card-title class="pa-3">
                        <v-spacer />
                        <v-btn size="small" @click="events = []">Clear</v-btn>
                    </v-card-title>
                    <v-card-text class="event-log-container">
                        <v-list dense class="event-log">
                            <v-list-item
                                v-for="(event, index) in events"
                                :key="index"
                                :class="`event-${(event as any).type}`"
                            >
                                <v-list-item-title>
                                    <v-icon
                                        :color="
                                            getEventColor((event as any).type)
                                        "
                                        size="small"
                                    >
                                        {{ getEventIcon((event as any).type) }}
                                    </v-icon>
                                    {{ formatEvent(event) }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ event.timestamp }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from "vue";
import { SequencePitchMatcher, type SequenceMatchEvent } from "../pitch";
import {
    createPitchDetector,
    DetectorType,
    type PitchDetectionResult,
} from "../pitch";
import PitchRecorderWidget from "../pitch/components/PitchRecorderWidget.vue";

// Configuration
const sequenceInput = ref("C4, D4, E4, F4, G4");
const toleranceCents = ref(40);
const allowOctaveErrors = ref(false);
const dropoutBufferFrames = ref(5);

// Pitch detection state
const audioContext = ref<AudioContext | null>(null);
const analyser = ref<AnalyserNode | null>(null);
const mediaStream = ref<MediaStream | null>(null);
const detector = createPitchDetector(DetectorType.BLUESHARP, "flute");
const isListening = ref(false);
const animationFrameId = ref<number | null>(null);
const detectedPitch = ref<PitchDetectionResult | null>(null);

// Matcher state
const matcher = ref<SequencePitchMatcher | null>(null);
const state = ref({
    currentIndex: 0,
    completedNotes: [] as string[],
    isComplete: false,
    progress: 0,
    wrongAttempts: 0,
});
const currentlyPlayingNote = ref<string | null>(null);
const currentlyPlayingIndex = ref<number | null>(null);

// Recorder widget
const recorderWidget = ref<InstanceType<typeof PitchRecorderWidget> | null>(null);

// Event log
interface LoggedEvent {
    timestamp: string;
    type: string;
    [key: string]: any;
}
const events = ref<LoggedEvent[]>([]);

// Computed
const sequence = computed(() => {
    return sequenceInput.value
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
});

const currentNote = computed(() => {
    return matcher.value?.getCurrentNote() || "—";
});

function isNoteCompleted(index: number): boolean {
    return matcher.value?.isNoteCompleted(index) ?? false;
}

function isCurrentNote(index: number): boolean {
    return matcher.value?.isCurrentNote(index) ?? false;
}

function isNotePlaying(index: number): boolean {
    return currentlyPlayingIndex.value === index;
}

// Audio functions
async function startListening() {
    try {
        // Create matcher with current config
        matcher.value = new SequencePitchMatcher({
            sequence: sequence.value,
            toleranceCents: toleranceCents.value,
            allowOctaveErrors: allowOctaveErrors.value,
            dropoutBufferFrames: dropoutBufferFrames.value,
        });
        updateState();

        // Request microphone access
        mediaStream.value = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
            },
        });

        // Create audio context
        audioContext.value = new AudioContext();
        const source = audioContext.value.createMediaStreamSource(
            mediaStream.value,
        );

        // Create analyzer
        analyser.value = audioContext.value.createAnalyser();
        analyser.value.fftSize = 2048;
        analyser.value.smoothingTimeConstant = 0.8;
        source.connect(analyser.value);

        isListening.value = true;

        // Start analysis loop
        analyzePitch();
    } catch (error) {
        console.error("Failed to access microphone:", error);
        alert("Could not access microphone. Please check permissions.");
    }
}

function stopListening() {
    isListening.value = false;
    detectedPitch.value = null;

    if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
    }

    if (mediaStream.value) {
        mediaStream.value.getTracks().forEach((track) => track.stop());
        mediaStream.value = null;
    }

    if (audioContext.value) {
        audioContext.value.close();
        audioContext.value = null;
    }

    analyser.value = null;
}

function analyzePitch() {
    if (
        !isListening.value ||
        !analyser.value ||
        !audioContext.value ||
        !matcher.value
    ) {
        return;
    }

    // Get audio data
    const buffer = new Float32Array(analyser.value.frequencyBinCount);
    analyser.value.getFloatTimeDomainData(buffer);

    // Detect pitch
    const result = detector.detectPitch(buffer, audioContext.value.sampleRate);
    detectedPitch.value = result;

    // Capture for recording if active
    recorderWidget.value?.capture(result);

    // Process pitch with matcher (always call, even for silence)
    const event = matcher.value.processPitch(result);

    if (event) {
        // Update currently playing note for visual feedback
        if (event.type === "note-matched" || event.type === "note-sustained") {
            currentlyPlayingNote.value = event.note;
            currentlyPlayingIndex.value = event.index;
        } else if (event.type === "wrong-note") {
            currentlyPlayingNote.value = event.detected;
            currentlyPlayingIndex.value = null;
        } else if (event.type === "note-ended") {
            currentlyPlayingNote.value = null;
            currentlyPlayingIndex.value = null;
        }

        // Log all events except sustained (avoid spam)
        if (event.type !== "note-sustained") {
            logEvent(event);
        }

        updateState();
    }

    // Clear visual feedback when no pitch detected
    // Note: We don't immediately clear on !result.frequency anymore
    // because the matcher now has a dropout buffer. Visual feedback
    // will be cleared when we get a 'note-ended' event instead.

    // Continue loop
    animationFrameId.value = requestAnimationFrame(analyzePitch);
}

function resetMatcher() {
    matcher.value?.reset();
    updateState();
    events.value = [];
    currentlyPlayingNote.value = null;
    currentlyPlayingIndex.value = null;
}

function updateState() {
    if (matcher.value) {
        const matcherState = matcher.value.getState();
        state.value = {
            currentIndex: matcherState.currentIndex,
            completedNotes: matcherState.completedNotes,
            isComplete: matcherState.isComplete,
            progress: matcherState.progress,
            wrongAttempts: matcherState.wrongAttempts || 0,
        };
    }
}

function logEvent(event: SequenceMatchEvent) {
    const timestamp = new Date().toLocaleTimeString();
    events.value.unshift({ ...event, timestamp });

    // Keep log size manageable
    if (events.value.length > 50) {
        events.value.pop();
    }
}

function formatEvent(event: LoggedEvent): string {
    const eventData = event as any;
    switch (eventData.type) {
        case "note-matched":
            return `Matched: ${eventData.note} (index ${eventData.index})`;
        case "note-sustained":
            return `Sustaining: ${eventData.note} (previous note)`;
        case "note-ended":
            return `Note ended: ${eventData.note} (silence detected)`;
        case "wrong-note":
            return `Wrong note: played ${eventData.detected}, expected ${eventData.expected}`;
        case "sequence-complete":
            return `Sequence complete! (${eventData.totalAttempts} wrong attempts)`;
        default:
            return "Unknown event";
    }
}

function getEventColor(type: string): string {
    switch (type) {
        case "note-matched":
            return "success";
        case "note-sustained":
            return "info";
        case "note-ended":
            return "grey";
        case "wrong-note":
            return "error";
        case "sequence-complete":
            return "primary";
        default:
            return "grey";
    }
}

function getEventIcon(type: string): string {
    switch (type) {
        case "note-matched":
            return "mdi-check-circle";
        case "note-sustained":
            return "mdi-music-note";
        case "note-ended":
            return "mdi-stop-circle";
        case "wrong-note":
            return "mdi-close-circle";
        case "sequence-complete":
            return "mdi-trophy";
        default:
            return "mdi-circle";
    }
}

onUnmounted(() => {
    stopListening();
});
</script>

<style scoped>
.debug-container {
    width: 100%;
    margin: 0;
    padding: 20px;
}

.layout {
    display: grid;
    grid-template-columns: 1fr 1.5fr 1fr;
    gap: 20px;
    height: calc(100vh - 200px);
    margin-bottom: 40px;
}

.left-panel,
.center-panel,
.right-panel {
    overflow-y: auto;
    padding: 20px;
    border-radius: 8px;
}

.sequence-display {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: center;
    padding: 20px;
}

.note-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px;
    border-radius: 8px;
    min-width: 80px;
    transition: all 0.3s ease;
}

.note-box.completed {
    background: rgba(76, 175, 80, 0.1);
    border: 2px solid rgb(76, 175, 80);
}

.note-box.current {
    background: rgba(33, 150, 243, 0.1);
    border: 2px solid rgb(33, 150, 243);
    box-shadow: 0 0 10px rgba(33, 150, 243, 0.3);
}

.note-box.playing {
    background: rgba(255, 152, 0, 0.15);
    border: 2px solid rgb(255, 152, 0);
    animation: pulse-playing 1s ease-in-out infinite;
}

.note-box.pending {
    background: rgba(158, 158, 158, 0.1);
    border: 2px solid rgb(158, 158, 158);
}

@keyframes pulse-playing {
    0%,
    100% {
        box-shadow: 0 0 5px rgba(255, 152, 0, 0.3);
    }
    50% {
        box-shadow: 0 0 15px rgba(255, 152, 0, 0.6);
    }
}

.note-label {
    font-size: 24px;
    font-weight: 600;
}

.event-log-container {
    max-height: 300px;
    overflow-y: auto;
}

.event-log {
    overflow-y: auto;
}

.event-note-matched {
    background: rgba(76, 175, 80, 0.05);
}

.event-wrong-note {
    background: rgba(244, 67, 54, 0.05);
}

.event-note-sustained {
    background: rgba(0, 188, 212, 0.05);
}

.event-sequence-complete {
    background: rgba(33, 150, 243, 0.05);
}
</style>
